import React, { useEffect, useState } from 'react';

import { UserActivityLog } from '../../types';
import { BarChart3, Users, Navigation, TrendingUp } from 'lucide-react';

interface UserStats {
    username: string;
    loginCount: number;
    navigationCount: number;
    mostVisitedPage: string;
    mostVisitedCount: number;
}

const UserAnalytics: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<UserStats[]>([]);

    useEffect(() => {
        loadLogs();
    }, []);

    const fetchActivityLogs = async (): Promise<UserActivityLog[]> => {
        // Mock implementation for local mode
        // In a real local implementation, we might read from localStorage
        const localLogs = localStorage.getItem('magnus_activity_logs');
        return localLogs ? JSON.parse(localLogs) : [];
    };

    const loadLogs = async () => {
        setLoading(true);
        const activityLogs = await fetchActivityLogs();
        calculateStats(activityLogs);
        setLoading(false);
    };

    const calculateStats = (activityLogs: UserActivityLog[]) => {
        const userMap = new Map<string, {
            loginCount: number;
            navigationCount: number;
            pageVisits: Map<string, number>;
        }>();

        // Process logs
        activityLogs.forEach(log => {
            if (!userMap.has(log.username)) {
                userMap.set(log.username, {
                    loginCount: 0,
                    navigationCount: 0,
                    pageVisits: new Map()
                });
            }

            const userData = userMap.get(log.username)!;

            if (log.activityType === 'login') {
                userData.loginCount++;
            } else if (log.activityType === 'navigation' && log.path) {
                userData.navigationCount++;
                const currentCount = userData.pageVisits.get(log.path) || 0;
                userData.pageVisits.set(log.path, currentCount + 1);
            }
        });

        // Convert to array and find most visited page for each user
        const statsArray: UserStats[] = [];
        userMap.forEach((data, username) => {
            let mostVisitedPage = 'N/A';
            let mostVisitedCount = 0;

            data.pageVisits.forEach((count, page) => {
                if (count > mostVisitedCount) {
                    mostVisitedCount = count;
                    mostVisitedPage = page;
                }
            });

            statsArray.push({
                username,
                loginCount: data.loginCount,
                navigationCount: data.navigationCount,
                mostVisitedPage,
                mostVisitedCount
            });
        });

        // Sort by total activity
        statsArray.sort((a, b) =>
            (b.loginCount + b.navigationCount) - (a.loginCount + a.navigationCount)
        );

        setStats(statsArray);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg text-text-secondary">Loading analytics...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-text-primary flex items-center gap-3">
                    <BarChart3 className="text-primary" />
                    User Analytics
                </h1>
                <p className="text-text-secondary mt-1">
                    Track user login counts and most used app pages
                </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <Users size={24} className="text-blue-500" />
                        <h3 className="font-semibold text-lg">Total Users</h3>
                    </div>
                    <p className="text-3xl font-bold text-text-primary">{stats.length}</p>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <TrendingUp size={24} className="text-green-500" />
                        <h3 className="font-semibold text-lg">Total Logins</h3>
                    </div>
                    <p className="text-3xl font-bold text-text-primary">
                        {stats.reduce((sum, s) => sum + s.loginCount, 0)}
                    </p>
                </div>

                <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center gap-3 mb-2">
                        <Navigation size={24} className="text-purple-500" />
                        <h3 className="font-semibold text-lg">Total Page Views</h3>
                    </div>
                    <p className="text-3xl font-bold text-text-primary">
                        {stats.reduce((sum, s) => sum + s.navigationCount, 0)}
                    </p>
                </div>
            </div>

            {/* User Table */}
            <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
                <div className="p-4 border-b border-border">
                    <h2 className="font-bold text-lg">User Activity Breakdown</h2>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-background">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                    Username
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                    Login Count
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                    Page Views
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                    Most Used Page
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                                    Visits
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {stats.map((user, index) => (
                                <tr key={user.username} className={index % 2 === 0 ? 'bg-background/50' : ''}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="font-medium text-text-primary">{user.username}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                                            {user.loginCount}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
                                            {user.navigationCount}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-text-primary font-mono">
                                            {user.mostVisitedPage}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                            {user.mostVisitedCount}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {stats.length === 0 && (
                    <div className="p-12 text-center text-text-secondary">
                        <p>No activity logs found. User activity will appear here once users start using the app.</p>
                    </div>
                )}
            </div>

            {/* Refresh Button */}
            <div className="flex justify-end">
                <button
                    onClick={loadLogs}
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                >
                    Refresh Data
                </button>
            </div>
        </div>
    );
};

export default UserAnalytics;
